// ============================================================================
// deriveOrgLevel — echelon d'ENTREPRISE affiche (MASTER-GEST-HIERARCHIE-ENTREPRISE)
// ============================================================================
// Source UNIQUE de derivation pour toute surface qui presente une personne
// comme membre de l'ENTREPRISE (organigramme, gouvernance, cartes d'equipe).
// Orthogonal au role APPLICATIF UCBR (derivePersonRole — ne PAS confondre).
//
// On ne stocke QUE ce qui ne peut pas se deriver (doctrine STOP-8 intacte) :
//   - 'gestionnaire' saisi sur gest.EmployeeProfiles.org_level
//   - 'directeur' DERIVE de Teams.director_email (isTeamDirector)
//   - 'employe' = absence d'echelon saisi + pas directeur, quand l'info est PORTEE
//
// Precedence :
//   1. orgLevel === 'gestionnaire'     → gestionnaire  (echelon saisi gagne)
//   2. isTeamDirector                  → directeur
//   3. orgLevel CONNU (null)           → employe
//   4. orgLevel ABSENT (undefined)     → null         (information non portee)
//
// Contrat orgLevel (DOCTRINE : jamais afficher un echelon FAUX) :
//   - `undefined` / cle absente = l'appelant N'A PAS la donnee
//     → on peut encore conclure `directeur` si isTeamDirector, sinon `null`.
//     JAMAIS `employe` par defaut.
//   - `null` = information CONNUE : aucun echelon saisi
//     → directeur si isTeamDirector, sinon employe.
//   - 'gestionnaire' = valeur connue du CHECK SQL.
//
// i18n : namespace DEDIE gest:orgLevel.* (jamais gest:personRole.*).

/** Valeurs saisies permises — miroir exact du CHECK SQL CK_gest_EmployeeProfiles_org_level. */
export const ORG_LEVEL_STORED_VALUES = ['gestionnaire'] as const;
export type OrgLevelStored = (typeof ORG_LEVEL_STORED_VALUES)[number];

export type DerivedOrgLevel = 'gestionnaire' | 'directeur' | 'employe';

export type OrgLevelInput = OrgLevelStored | null | undefined;

export interface DeriveOrgLevelInput {
  /**
   * Echelon saisi (EmployeeProfiles.org_level).
   * - omit / `undefined` = non porte par le DTO (ne pas conclure employe)
   * - `null` = porte et connu : aucun echelon saisi
   */
  orgLevel?: OrgLevelInput;
  /** True si l'email figure dans au moins un gest.Teams.director_email actif. */
  isTeamDirector?: boolean;
}

/**
 * Derive l'echelon d'affichage entreprise.
 * Retourne `null` quand orgLevel n'est pas porte et qu'on ne peut pas conclure
 * autrement (ex. directeur d'equipe) — l'UI doit alors laisser un trou plutot
 * que d'afficher « Employe ».
 */
export function deriveOrgLevel(input: DeriveOrgLevelInput): DerivedOrgLevel | null {
  if (input.orgLevel === 'gestionnaire') return 'gestionnaire';
  if (input.isTeamDirector) return 'directeur';
  // Information non portee : ne PAS inventer employe.
  if (input.orgLevel === undefined) return null;
  // Information connue (null) → employe.
  return 'employe';
}

/** Cle i18n FR+EN pour le libelle de l'echelon derive (`gest:orgLevel.*`). */
export function orgLevelI18nKey(level: DerivedOrgLevel): `gest:orgLevel.${DerivedOrgLevel}` {
  return `gest:orgLevel.${level}`;
}

/** Narrow defensif : valeur hors CHECK → null (aucun echelon saisi). */
export function toOrgLevelStored(value: string | null | undefined): OrgLevelStored | null {
  if (value == null) return null;
  return (ORG_LEVEL_STORED_VALUES as readonly string[]).includes(value)
    ? (value as OrgLevelStored)
    : null;
}
